import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Async Thunks for Template API Calls
 */

// Fetch all templates with filters
export const fetchTemplates = createAsyncThunk(
  'templates/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/templates', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Fetch single template with question tree
export const fetchTemplateById = createAsyncThunk(
  'templates/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/templates/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Create template with questions (transaction-based)
export const createTemplate = createAsyncThunk(
  'templates/create',
  async ({ template, questions }, { rejectWithValue }) => {
    try {
      const response = await api.post('/templates', { template, questions });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Update template with questions
export const updateTemplate = createAsyncThunk(
  'templates/update',
  async ({ id, template, questions }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/templates/${id}`, {
        template,
        questions,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Delete template (soft delete)
export const deleteTemplate = createAsyncThunk(
  'templates/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/templates/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Template Slice
 */
const templateSlice = createSlice({
  name: 'templates',
  initialState: {
    templates: [],
    currentTemplate: null,
    questionTree: [],
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0,
    },
  },
  reducers: {
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
      state.questionTree = [];
    },
    setCurrentTemplate: (state, action) => {
      state.currentTemplate = action.payload;
    },
    updateQuestionTree: (state, action) => {
      state.questionTree = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Templates
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.templates || [];
        
        // Ensure minimum limit of 10
        const backendPagination = action.payload.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0,
        };
        
        // If limit is less than 10, set to 20 (default)
        const safeLimit = backendPagination.limit && backendPagination.limit >= 10 
          ? backendPagination.limit 
          : 20;
        
        state.pagination = {
          ...backendPagination,
          limit: safeLimit,
        };
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch templates';
      });

    // Fetch Template By ID
    builder
      .addCase(fetchTemplateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTemplate = action.payload;
        state.questionTree = action.payload.questionTree || [];
      })
      .addCase(fetchTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch template';
      });

    // Create Template
    builder
      .addCase(createTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTemplate = action.payload.template;
        state.questionTree = action.payload.questionTree || [];
        // Add to templates list
        state.templates.unshift(action.payload.template);
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to create template';
      });

    // Update Template
    builder
      .addCase(updateTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTemplate = action.payload.template;
        state.questionTree = action.payload.questionTree || [];
        // Update in templates list
        const index = state.templates.findIndex(
          (t) => t._id === action.payload.template._id
        );
        if (index !== -1) {
          state.templates[index] = action.payload.template;
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update template';
      });

    // Delete Template
    builder
      .addCase(deleteTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from templates list
        state.templates = state.templates.filter(
          (t) => t._id !== action.payload
        );
        // Clear current if deleted
        if (state.currentTemplate?._id === action.payload) {
          state.currentTemplate = null;
          state.questionTree = [];
        }
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete template';
      });
  },
});

export const {
  clearCurrentTemplate,
  setCurrentTemplate,
  updateQuestionTree,
  clearError,
} = templateSlice.actions;

export default templateSlice.reducer;

